import React, { useState, useEffect } from 'react';
import { Users, Shield, Plus, Edit, Trash2, Search, X, Check, AlertTriangle, Settings, UserX, UserCheck } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';


const ROLE_HIERARCHY = [
  { 
    value: 'preparer', 
    label: 'Preparer', 
    level: 1,
    description: 'Can create and edit own draft calculations',
    color: 'bg-slate-100 text-slate-700 border-slate-300'
  },
  { 
    value: 'account_executive', 
    label: 'Account Executive', 
    level: 2,
    description: 'Can create, submit proposals, view only own data',
    color: 'bg-blue-100 text-blue-700 border-blue-300'
  },
  { 
    value: 'approver', 
    label: 'Approver (Manager)', 
    level: 3,
    description: 'Can approve standard and manager-level proposals, view all submissions',
    color: 'bg-orange-100 text-orange-700 border-orange-300'
  },
  { 
    value: 'cfo', 
    label: 'CFO', 
    level: 4,
    description: 'Can approve high-value proposals, full analytics access',
    color: 'bg-purple-100 text-purple-700 border-purple-300'
  },
  { 
    value: 'ceo', 
    label: 'CEO', 
    level: 5,
    description: 'Can approve critical proposals, full system access',
    color: 'bg-red-100 text-red-700 border-red-300'
  },
  { 
    value: 'admin', 
    label: 'Administrator', 
    level: 6,
    description: 'Full system administration access, manage users and settings',
    color: 'bg-indigo-100 text-indigo-700 border-indigo-300'
  },
];

const PERMISSIONS_BY_ROLE = {
  preparer: [
    'Create calculations',
    'Edit own drafts',
    'Delete own drafts',
    'View own calculations',
    'View own analytics',
    '🚫 RESTRICTED: Cannot access Settings Manager',
    '🚫 RESTRICTED: Cannot access Manage Services',
    '🚫 RESTRICTED: Cannot access Account Executives',
    '🚫 RESTRICTED: Cannot access Fee Table Analytics'
  ],
  account_executive: [
    'Create calculations',
    'Edit own drafts',
    'Delete own drafts',
    'Submit for approval',
    'View own calculations',
    'View own analytics',
    '🚫 RESTRICTED: Cannot access Settings Manager',
    '🚫 RESTRICTED: Cannot access Manage Services',
    '🚫 RESTRICTED: Cannot access Account Executives',
    '🚫 RESTRICTED: Cannot access Fee Table Analytics'
  ],
  approver: [
    'Create calculations',
    'View all submitted proposals',
    'Approve standard proposals',
    'Approve manager-level proposals',
    'Edit own calculations',
    '✅ VIEW: Analytics & Reports',
    'View management dashboard',
    '🚫 RESTRICTED: Cannot manage settings or users'
  ],
  cfo: [
    'Create calculations',
    'View all proposals',
    'Edit all proposals',
    'Delete all proposals',
    'Approve CEO-level proposals',
    'Approve all proposal levels',
    '✅ VIEW: Analytics & Reports',
    'View management dashboard',
    'View audit logs',
    '🚫 RESTRICTED: Cannot manage settings or users'
  ],
  ceo: [
    'Create calculations',
    'View all proposals',
    'Edit all proposals',
    'Delete all proposals',
    'Approve CEO-level proposals',
    'Approve all proposal levels',
    '✅ VIEW: Analytics & Reports',
    'View management dashboard',
    'View audit logs',
    '🚫 RESTRICTED: Cannot manage settings or users'
  ],
  admin: [
    'Full system access',
    '✅ VIEW: All menu items visible',
    '✅ MANAGE: User Management',
    '✅ MANAGE: Settings Manager',
    '✅ MANAGE: Manage Services',
    '✅ MANAGE: Merchant Fee Manager',
    '✅ MANAGE: Account Executives',
    '✅ MANAGE: Fee Table Analytics',
    'Configure approval thresholds',
    'View audit logs',
    'All approval permissions',
    'All data access'
  ]
};

// All available permissions that can be customized
const ALL_PERMISSIONS = [
  { id: 'create_calculations', label: 'Create calculations' },
  { id: 'edit_own_drafts', label: 'Edit own drafts' },
  { id: 'edit_all_calculations', label: 'Edit all calculations' },
  { id: 'delete_own_drafts', label: 'Delete own drafts' },
  { id: 'delete_all_calculations', label: 'Delete all calculations' },
  { id: 'submit_for_approval', label: 'Submit for approval' },
  { id: 'view_own_calculations', label: 'View own calculations' },
  { id: 'view_all_calculations', label: 'View all calculations' },
  { id: 'view_own_analytics', label: 'View own analytics' },
  { id: 'view_all_analytics', label: 'View all analytics' },
  { id: 'view_management_dashboard', label: 'View management dashboard' },
  { id: 'approve_standard', label: 'Approve standard proposals' },
  { id: 'approve_manager_level', label: 'Approve manager-level proposals' },
  { id: 'approve_cfo_level', label: 'Approve CFO-level proposals' },
  { id: 'approve_ceo_level', label: 'Approve CEO-level proposals' },
  { id: 'approve_all_levels', label: 'Approve all proposal levels' },
  { id: 'manage_users', label: 'Manage users' },
  { id: 'manage_roles', label: 'Manage roles' },
  { id: 'manage_settings', label: 'Manage settings' },
  { id: 'manage_services', label: 'Manage services' },
  { id: 'configure_approval_thresholds', label: 'Configure approval thresholds' },
  { id: 'view_audit_logs', label: 'View audit logs' },
  { id: 'export_data', label: 'Export data' },
  { id: 'full_system_access', label: 'Full system access' },
];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [customPermissions, setCustomPermissions] = useState([]);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'preparer',
    password: ''
  });

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUserRole(storedUser.role);
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Force cache break with version and timestamp
      const version = 'v3'; // Change this when users aren't loading
      const timestamp = new Date().getTime();
      const response = await api.get(`/admin/users?v=${version}&_nocache=${timestamp}`);
      
      console.log('='.repeat(80));
      console.log('[UserManagement] FRESH API FETCH');
      console.log('Total users received:', response.data.users?.length || 0);
      console.log('User list:', response.data.users?.map(u => `${u.name} (${u.email})`));
      console.log('='.repeat(80));
      
      const userCount = response.data.users?.length || 0;
      setUsers(response.data.users || []);
      
      // Show detailed toast with count
      toast.success(`✅ Loaded ${userCount} users from database`, {
        duration: 5000,
        description: userCount === 8 ? 'All users loaded successfully!' : `Expected 8 users, got ${userCount}`
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setModalMode('create');
    setFormData({
      name: '',
      email: '',
      role: 'preparer',
      password: ''
    });
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'preparer',
      password: ''
    });
    setShowModal(true);
  };

  const handleManagePermissions = (user) => {
    setSelectedUser(user);
    setCustomPermissions(user.custom_permissions || []);
    setShowPermissionsModal(true);
  };

  const togglePermission = (permissionId) => {
    setCustomPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(p => p !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const saveCustomPermissions = async () => {
    if (!selectedUser) return;

    try {
      // Support both user_id and id fields
      const userId = selectedUser.user_id || selectedUser.id;
      if (!userId) {
        throw new Error('User ID not found');
      }
      
      await api.put(
        `/admin/users/${userId}`,
        {
          custom_permissions: customPermissions
        }
      );
      
      toast.success('Custom permissions updated successfully');
      setShowPermissionsModal(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleToggleUserStatus = async (user) => {
    const action = user.active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} ${user.name || user.email}?`)) {
      return;
    }

    try {
      // Support both user_id and id fields
      const userId = user.user_id || user.id;
      if (!userId) {
        throw new Error('User ID not found');
      }
      
      await api.patch(`/admin/users/${userId}/toggle-active`, {});
      
      toast.success(`User ${action}d successfully`);
      await fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const handleStatusChange = async (user, newStatus) => {
    const isActive = newStatus === 'active';
    
    // If status is already the same, do nothing
    if ((user.active !== false && isActive) || (user.active === false && !isActive)) {
      return;
    }

    try {
      // Support both user_id and id fields
      const userId = user.user_id || user.id;
      if (!userId) {
        throw new Error('User ID not found');
      }
      
      await api.patch(`/admin/users/${userId}/toggle-active`, {});
      
      toast.success(`User ${isActive ? 'activated' : 'deactivated'} successfully`);
      await fetchUsers();
    } catch (error) {
      console.error('Error changing user status:', error);
      toast.error(error.response?.data?.detail || 'Failed to change user status');
    }
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      // Support both user_id and id fields
      const userId = userToDelete.user_id || userToDelete.id;
      if (!userId) {
        throw new Error('User ID not found');
      }
      
      await api.delete(`/admin/users/${userId}`);
      
      toast.success('User deleted successfully');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      
      if (modalMode === 'create') {
        await api.post(
          '/admin/users',
          formData
        );
        toast.success('User created successfully');
      } else {
        // Support both user_id and id fields
        const userId = selectedUser.user_id || selectedUser.id;
        if (!userId) {
          throw new Error('User ID not found');
        }
        
        await api.put(
          `/admin/users/${userId}`,
          {
            name: formData.name,
            email: formData.email,
            role: formData.role,
            ...(formData.password && { password: formData.password })
          }
        );
        toast.success('User updated successfully');
      }
      
      setShowModal(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const getRoleInfo = (roleValue) => {
    return ROLE_HIERARCHY.find(r => r.value === roleValue) || ROLE_HIERARCHY[0];
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (user.name || '').toLowerCase().includes(searchLower) ||
      (user.email || '').toLowerCase().includes(searchLower) ||
      (user.role || '').toLowerCase().includes(searchLower)
    );
  });

  // DEBUG: Log filtering results
  console.log('[UserManagement] Filtering:', {
    totalUsers: users.length,
    filteredUsers: filteredUsers.length,
    searchTerm: searchTerm,
    userEmails: users.map(u => u.email),
    filteredEmails: filteredUsers.map(u => u.email)
  });

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">User Management</h1>
              <p className="text-sm text-slate-400 mt-0.5">Manage users, roles, and permissions</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchUsers}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
                title="Refresh user list from database"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh ({users.length} users)
              </button>
              <button
                onClick={handleCreateUser}
                className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors text-sm font-medium shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Add User
              </button>
            </div>
          </div>

          {/* Debug Banner - Show if not all users loaded */}
          {users.length < 8 && users.length > 0 && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-red-900 font-semibold mb-1">⚠️ Not All Users Loaded</h3>
                  <p className="text-red-800 text-sm mb-2">
                    Only {users.length} of 8 users are showing. This is likely due to browser caching.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={fetchUsers}
                      className="text-sm bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-medium"
                    >
                      Force Refresh Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or role..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Role Hierarchy Card */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            Role Hierarchy & Permissions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ROLE_HIERARCHY.map((role) => (
              <div key={role.value} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full border ${role.color}`}>
                    {role.label}
                  </span>
                  <span className="text-xs text-slate-500">Level {role.level}</span>
                </div>
                <p className="text-sm text-slate-600 mb-3">{role.description}</p>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Permissions:</p>
                  {PERMISSIONS_BY_ROLE[role.value]?.map((permission, idx) => (
                    <div key={`${role.value}-permission-${idx}`} className="flex items-start gap-1 text-xs text-slate-600">
                      <Check className="w-3 h-3 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{permission}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Level</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const roleInfo = getRoleInfo(user.role);
                    return (
                      <tr key={user.user_id || user.id} className={`hover:bg-slate-50 transition-colors ${!user.active ? 'opacity-60' : ''}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-slate-900">{user.name || 'N/A'}</p>
                                {user.active === false && (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                                    <UserX className="w-3 h-3" />
                                    Inactive
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500">{roleInfo.description}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{user.email}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full border ${roleInfo.color}`}>
                            {roleInfo.label}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={user.active === false ? 'inactive' : 'active'}
                            onChange={(e) => handleStatusChange(user, e.target.value)}
                            className={`text-xs font-medium px-3 py-1.5 rounded-lg border-2 transition-colors cursor-pointer ${
                              user.active === false
                                ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                                : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                            }`}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-600">Level {roleInfo.level}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleToggleUserStatus(user)}
                              className={`p-2 rounded-lg transition-colors ${
                                user.active === false 
                                  ? 'text-green-600 hover:bg-green-50' 
                                  : 'text-orange-600 hover:bg-orange-50'
                              }`}
                              title={user.active === false ? 'Activate user' : 'Deactivate user (soft lockout)'}
                            >
                              {user.active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleManagePermissions(user)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Manage custom permissions"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit user"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">
                {modalMode === 'create' ? 'Create New User' : 'Edit User'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Role <span className="text-red-600">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {ROLE_HIERARCHY.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label} (Level {role.level})
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-slate-500">
                  {getRoleInfo(formData.role).description}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password {modalMode === 'create' && <span className="text-red-600">*</span>}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={modalMode === 'create'}
                  className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder={modalMode === 'edit' ? 'Leave blank to keep unchanged' : 'Enter password'}
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  {modalMode === 'create' ? 'Create User' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Delete User</h3>
                <p className="text-sm text-slate-600">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-slate-700 mb-6">
              Are you sure you want to delete <strong>{userToDelete.name || userToDelete.email}</strong>?
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-3xl w-full p-6 my-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Custom Permissions</h3>
                <p className="text-sm text-slate-600 mt-1">
                  {selectedUser.name || selectedUser.email} - {getRoleInfo(selectedUser.role).label}
                </p>
              </div>
              <button
                onClick={() => setShowPermissionsModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> Custom permissions allow you to grant additional capabilities beyond the user's base role. 
                These permissions are added on top of the role's default permissions.
              </p>
            </div>

            {/* Default Role Permissions */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-indigo-600" />
                Default {getRoleInfo(selectedUser.role).label} Permissions
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {PERMISSIONS_BY_ROLE[selectedUser.role]?.map((permission, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{permission}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Permissions Toggles */}
            <div className="mb-6">
              <h4 className="font-semibold text-slate-900 mb-3">Additional Custom Permissions</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto p-2">
                {ALL_PERMISSIONS.map((permission) => {
                  const isEnabled = customPermissions.includes(permission.id);
                  return (
                    <label
                      key={permission.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isEnabled
                          ? 'bg-indigo-50 border-indigo-500'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => togglePermission(permission.id)}
                        className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                      />
                      <span className={`text-sm ${isEnabled ? 'text-indigo-900 font-medium' : 'text-slate-700'}`}>
                        {permission.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Summary */}
            {customPermissions.length > 0 && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-2">
                  {customPermissions.length} Custom Permission{customPermissions.length !== 1 ? 's' : ''} Selected
                </h4>
                <div className="flex flex-wrap gap-2">
                  {customPermissions.map((permId) => {
                    const perm = ALL_PERMISSIONS.find(p => p.id === permId);
                    return (
                      <span key={permId} className="text-xs bg-white px-2 py-1 rounded-full border border-green-300 text-green-800">
                        {perm?.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowPermissionsModal(false)}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveCustomPermissions}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
